/**
 * SubRip (.srt) and WebVTT (.vtt) subtitles: reading, retiming and writing.
 *
 * Times are integer milliseconds throughout. Retiming is exact: a scale factor is kept as
 * a ratio of integers (a typed `0.95904` is 95904/100000, a frame-rate change is
 * 24000/25000 or 25×1001/24000), and a cue time is rounded once, half up, at the end — so
 * 100 cues do not drift by accumulated floating-point error and the last cue lands where
 * the arithmetic says it should.
 *
 * Format facts follow the W3C WebVTT specification (https://www.w3.org/TR/webvtt1/):
 * a `WEBVTT` header line, timestamps with a full stop before the milliseconds and an
 * optional hours part, cue settings after the end time, and `&amp;`, `&lt;` escapes in
 * cue text. SubRip numbers each cue and writes `HH:MM:SS,mmm` with a comma.
 */

export type SubtitleFormat = "srt" | "vtt";

export interface SubtitleCue {
  /** The cue identifier (VTT) or sequence number (SRT) as it appeared. */
  id: string | null;
  start: number;
  end: number;
  /** WebVTT cue settings (`align:start position:10%`), kept as text. */
  settings: string;
  text: string;
}

export interface ParsedSubtitles {
  format: SubtitleFormat | null;
  cues: SubtitleCue[];
}

const TIME = "(?:(\\d{1,}):)?(\\d{1,2}):(\\d{2})[.,](\\d{1,3})";
const RE_TIMING = new RegExp(`^\\s*${TIME}\\s*-->\\s*${TIME}(?:\\s+(.*))?$`);
const RE_TIME_ONLY = new RegExp(`^\\s*${TIME}\\s*$`);
const RE_HEADER = /^\uFEFF?WEBVTT(?:[ \t].*)?$/;
const RE_LINES = /\r\n|\r|\n/;
const RE_BOM = /^\uFEFF/;
const RE_SRT_COMMA = /\d,\d{1,3}\s*-->/;
const RE_BLOCK_SEPARATOR = /\n[ \t]*\n/;
const RE_NOTE_BLOCK = /^(?:NOTE|STYLE|REGION)(?:[ \t]|$)/;
const RE_NUMERIC = /^\d+$/;
const MS_SECOND = 1000;
const MS_MINUTE = 60_000;
const MS_HOUR = 3_600_000;
const FRACTION_DIGITS = 3;

function toMs(
  hours: string | undefined,
  minutes: string | undefined,
  seconds: string | undefined,
  fraction: string | undefined
): number {
  return (
    Number(hours ?? 0) * MS_HOUR +
    Number(minutes) * MS_MINUTE +
    Number(seconds) * MS_SECOND +
    Number((fraction ?? "0").padEnd(FRACTION_DIGITS, "0"))
  );
}

/** `01:02:03,456`, `02:03.456`, `1:02:03.5` → milliseconds, or null. */
export function parseSubtitleTime(value: string): number | null {
  const m = value.match(RE_TIME_ONLY);
  return m ? toMs(m[1], m[2], m[3], m[4]) : null;
}

const RE_CLOCK = /^(?:(\d+):)?(\d{1,2}):(\d{2})(?:[.,](\d{1,3}))?$/;

/**
 * A time a person types: `1:02:03`, `02:03.5`, `00:01:02,250`, or plain seconds (`62.25`).
 * Returns milliseconds, or null when it is not a time.
 */
export function parseTimeInput(raw: string): number | null {
  const text = raw.trim();
  const clock = text.match(RE_CLOCK);
  if (clock) {
    return toMs(clock[1], clock[2], clock[3], clock[4]);
  }
  const seconds = parseRatio(text);
  if (!seconds || seconds.num < 0) {
    return null;
  }
  return roundDiv(seconds.num * MS_SECOND, seconds.den);
}

function parseTimingLine(line: string) {
  const m = line.match(RE_TIMING);
  if (!m) {
    return null;
  }
  return {
    start: toMs(m[1], m[2], m[3], m[4]),
    end: toMs(m[5], m[6], m[7], m[8]),
    settings: (m[9] ?? "").trim(),
  };
}

export function detectSubtitleFormat(input: string): SubtitleFormat | null {
  const first = input.replace(RE_BOM, "").trimStart();
  if (first.startsWith("WEBVTT")) {
    return "vtt";
  }
  if (RE_SRT_COMMA.test(input)) {
    return "srt";
  }
  return input.split(RE_LINES).some((line) => RE_TIMING.test(line))
    ? "vtt"
    : null;
}

function parseVttBlocks(text: string): SubtitleCue[] {
  const cues: SubtitleCue[] = [];
  for (const block of text.split(RE_BLOCK_SEPARATOR)) {
    const lines = block.split("\n");
    const first = lines[0] ?? "";
    if (RE_HEADER.test(first) || RE_NOTE_BLOCK.test(first)) {
      continue;
    }
    const at = lines.findIndex((line) => RE_TIMING.test(line));
    const timing = at >= 0 ? parseTimingLine(lines[at] ?? "") : null;
    if (!timing) {
      continue;
    }
    cues.push({
      id: at > 0 ? lines.slice(0, at).join(" ").trim() || null : null,
      ...timing,
      text: lines
        .slice(at + 1)
        .join("\n")
        .trim(),
    });
  }
  return cues;
}

/** SubRip is looser than VTT: a cue's text may be broken by a stray blank line. */
function parseSrtLines(text: string): SubtitleCue[] {
  const lines = text.split("\n");
  const timingAt = lines.flatMap((line, index) =>
    RE_TIMING.test(line) ? [index] : []
  );
  return timingAt.flatMap((at, n) => {
    const timing = parseTimingLine(lines[at] ?? "");
    if (!timing) {
      return [];
    }
    const before = lines[at - 1]?.trim() ?? "";
    const hasId = before !== "" && at - 1 > (timingAt[n - 1] ?? -1);
    const next = timingAt[n + 1];
    let stop = next ?? lines.length;
    if (next !== undefined && (lines[next - 1]?.trim() ?? "") !== "") {
      stop = next - 1;
    }
    return [
      {
        id: hasId ? before : null,
        ...timing,
        text: lines
          .slice(at + 1, stop)
          .join("\n")
          .trim(),
      },
    ];
  });
}

export function parseSubtitles(input: string): ParsedSubtitles {
  const format = detectSubtitleFormat(input);
  const text = input.replace(RE_BOM, "").split(RE_LINES).join("\n");
  if (format === null) {
    return { format, cues: [] };
  }
  return {
    format,
    cues: format === "vtt" ? parseVttBlocks(text) : parseSrtLines(text),
  };
}

// ---------------------------------------------------------------------------
// Retiming

export interface Ratio {
  num: number;
  den: number;
}

const RE_DECIMAL = /^([+-])?(\d*)(?:\.(\d+))?$/;
const MAX_DECIMALS = 9;

/** An exact decimal: "-1.5" → -3/2 as -15/10; a decimal comma is accepted. */
export function parseRatio(raw: string): Ratio | null {
  const clean = raw.trim().replace("−", "-").replace(",", ".");
  const m = clean.match(RE_DECIMAL);
  if (!m || (m[2] === "" && m[3] === undefined)) {
    return null;
  }
  const fraction = m[3] ?? "";
  if (fraction.length > MAX_DECIMALS) {
    return null;
  }
  const magnitude = Number(`${m[2] || "0"}${fraction}`);
  return {
    num: m[1] === "-" ? -magnitude : magnitude,
    den: 10 ** fraction.length,
  };
}

/** `n / d` rounded to the nearest integer, halves up. */
export function roundDiv(n: number, d: number): number {
  return Math.floor((2 * n + d) / (2 * d));
}

/** `a / b` rounded half up in exact integer arithmetic, for products too big for a double. */
function roundDivBig(a: bigint, b: bigint): number {
  const zero = BigInt(0);
  const two = BigInt(2);
  const numerator = two * a + b;
  const denominator = two * b;
  let quotient = numerator / denominator;
  if (numerator % denominator < zero) {
    quotient -= BigInt(1);
  }
  return Number(quotient);
}

/** `target + round((t − pivot) × scale)`, the one formula every retiming reduces to. */
export interface Retime {
  pivot: number;
  scale: Ratio;
  target: number;
}

export const NO_RETIME: Retime = {
  pivot: 0,
  scale: { num: 1, den: 1 },
  target: 0,
};

export function applyRetime(time: number, retime: Retime): number {
  const { num, den } = retime.scale;
  const product = (time - retime.pivot) * num;
  if (Number.isSafeInteger(2 * product + den)) {
    return retime.target + roundDiv(product, den);
  }
  return (
    retime.target +
    roundDivBig(BigInt(time - retime.pivot) * BigInt(num), BigInt(den))
  );
}

/** A constant offset in milliseconds. */
export function shiftBy(ms: number): Retime {
  return { ...NO_RETIME, target: ms };
}

/** Multiply every time by a ratio of integers. */
export function stretchBy(scale: Ratio): Retime | null {
  return scale.num > 0 && scale.den > 0 ? { ...NO_RETIME, scale } : null;
}

export interface FrameRate {
  label: string;
  num: number;
  den: number;
}

/** The NTSC rates are exact fractions, which is why 23.976 is 24000/1001. */
export const FRAME_RATES: readonly FrameRate[] = [
  { label: "23.976", num: 24_000, den: 1001 },
  { label: "24", num: 24, den: 1 },
  { label: "25", num: 25, den: 1 },
  { label: "29.97", num: 30_000, den: 1001 },
  { label: "30", num: 30, den: 1 },
  { label: "50", num: 50, den: 1 },
  { label: "59.94", num: 60_000, den: 1001 },
  { label: "60", num: 60, den: 1 },
];

/** Subtitles timed for video at `from` fps, for the same frames played at `to` fps. */
export function frameRateRetime(from: FrameRate, to: FrameRate): Retime | null {
  return stretchBy({ num: from.num * to.den, den: from.den * to.num });
}

/** Map the first cue's start to `firstTo` and the last cue's start to `lastTo`. */
export function syncRetime(
  firstFrom: number,
  lastFrom: number,
  firstTo: number,
  lastTo: number
): Retime | null {
  const span = lastFrom - firstFrom;
  const target = lastTo - firstTo;
  if (span <= 0 || target <= 0) {
    return null;
  }
  return {
    pivot: firstFrom,
    scale: { num: target, den: span },
    target: firstTo,
  };
}

export interface RetimeResult {
  cues: SubtitleCue[];
  /** Each kept cue's start before retiming, in the same order as `cues`. */
  originalStarts: number[];
  /** Cues that ended before time zero and were removed. */
  dropped: number;
  /** Cues whose start was pulled up to zero. */
  clamped: number;
}

export function retimeCues(
  cues: readonly SubtitleCue[],
  retime: Retime
): RetimeResult {
  const result: SubtitleCue[] = [];
  const originalStarts: number[] = [];
  let dropped = 0;
  let clamped = 0;
  for (const cue of cues) {
    const start = applyRetime(cue.start, retime);
    const end = applyRetime(cue.end, retime);
    if (end <= 0) {
      dropped += 1;
      continue;
    }
    clamped += start < 0 ? 1 : 0;
    const from = Math.max(0, start);
    result.push({ ...cue, start: from, end: Math.max(end, from + 1) });
    originalStarts.push(cue.start);
  }
  return { cues: result, originalStarts, dropped, clamped };
}

// ---------------------------------------------------------------------------
// Writing

export interface WriteOptions {
  /** Keep the cue ids the source had (WebVTT output). */
  keepIds: boolean;
  /** Number WebVTT cues 1, 2, 3… instead of keeping their ids (SubRip is always numbered). */
  renumber: boolean;
  /** Keep WebVTT cue settings (WebVTT output from WebVTT input). */
  keepSettings: boolean;
  /** Remove every formatting tag, leaving plain text. */
  stripTags: boolean;
  /** Write `<v Name>` voice tags as `Name: ` when producing SubRip. */
  voiceToLabel: boolean;
}

const RE_VOICE_OPEN = /<v(?:\.[^\s>]*)*[ \t]+([^>]+)>/g;
const RE_VOICE_CLOSE = /<\/v>/g;
const RE_TIMESTAMP_TAG = /<\d{1,}:\d{2}(?::\d{2})?\.\d{3}>/g;
const RE_VTT_ONLY_TAG =
  /<\/?(?:c|lang|ruby|rt|rp|v)(?:\.[^\s>]*)*(?:[ \t][^>]*)?>/g;
const RE_FONT = /<\/?font[^>]*>/gi;
const RE_ASS = /\{\\[^}]*\}/g;
const RE_ANY_TAG = /<[^>]*>/g;
const RE_KEPT_TAG = /^<\/?[ibu]>$/i;
const RE_AMP = /&(?!(?:amp|lt|gt|nbsp|lrm|rlm|#\d+|#x[\da-f]+);)/gi;
const RE_TAG_SPLIT = /(<\/?[ibu]>)/i;
const RE_ARROW = /-->/g;
const RE_LT = /</g;
const ENTITIES: Record<string, string> = {
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&nbsp;": " ",
  "&lrm;": "‎",
  "&rlm;": "‏",
};
const RE_ENTITY = /&(?:amp|lt|gt|nbsp|lrm|rlm);/g;

function srtText(
  text: string,
  from: SubtitleFormat | null,
  options: WriteOptions
): string {
  let out = text;
  if (from === "vtt") {
    out = options.voiceToLabel ? out.replace(RE_VOICE_OPEN, "$1: ") : out;
    out = out
      .replace(RE_VOICE_CLOSE, "")
      .replace(RE_TIMESTAMP_TAG, "")
      .replace(RE_VTT_ONLY_TAG, "")
      .replace(RE_ENTITY, (entity) => ENTITIES[entity] ?? entity);
  }
  return options.stripTags ? out.replace(RE_ANY_TAG, "") : out;
}

/** Escape what WebVTT cue text may not contain, keeping `<i>`, `<b>` and `<u>`. */
function vttText(
  text: string,
  from: SubtitleFormat | null,
  options: WriteOptions
): string {
  if (from === "vtt") {
    const same = text.replace(RE_ARROW, "--&gt;");
    return options.stripTags
      ? same.replace(RE_ANY_TAG, "").replace(RE_ENTITY, (e) => ENTITIES[e] ?? e)
      : same;
  }
  const plain = text.replace(RE_ASS, "").replace(RE_FONT, "");
  if (options.stripTags) {
    return plain
      .replace(RE_ANY_TAG, "")
      .replace(RE_AMP, "&amp;")
      .replace(RE_LT, "&lt;")
      .replace(RE_ARROW, "--&gt;");
  }
  return plain
    .split(RE_TAG_SPLIT)
    .map((part) =>
      RE_KEPT_TAG.test(part)
        ? part.toLowerCase()
        : part
            .replace(RE_AMP, "&amp;")
            .replace(RE_LT, "&lt;")
            .replace(RE_ARROW, "--&gt;")
    )
    .join("");
}

const two = (n: number) => String(n).padStart(2, "0");

export function formatSubtitleTime(ms: number, separator: "," | "."): string {
  const hours = Math.floor(ms / MS_HOUR);
  const minutes = Math.floor((ms % MS_HOUR) / MS_MINUTE);
  const seconds = Math.floor((ms % MS_MINUTE) / MS_SECOND);
  const millis = String(ms % MS_SECOND).padStart(FRACTION_DIGITS, "0");
  return `${two(hours)}:${two(minutes)}:${two(seconds)}${separator}${millis}`;
}

export function writeSrt(
  cues: readonly SubtitleCue[],
  from: SubtitleFormat | null,
  options: WriteOptions
): string {
  return cues
    .map((cue, index) =>
      [
        String(index + 1),
        `${formatSubtitleTime(cue.start, ",")} --> ${formatSubtitleTime(cue.end, ",")}`,
        srtText(cue.text, from, options),
      ].join("\n")
    )
    .join("\n\n");
}

function vttIdentifier(
  cue: SubtitleCue,
  index: number,
  options: WriteOptions
): string[] {
  if (options.renumber) {
    return [String(index + 1)];
  }
  return options.keepIds && cue.id ? [cue.id] : [];
}

export function writeVtt(
  cues: readonly SubtitleCue[],
  from: SubtitleFormat | null,
  options: WriteOptions
): string {
  const blocks = cues.map((cue, index) => {
    const timing = `${formatSubtitleTime(cue.start, ".")} --> ${formatSubtitleTime(cue.end, ".")}`;
    const settings =
      from === "vtt" && options.keepSettings && cue.settings
        ? ` ${cue.settings}`
        : "";
    const head = vttIdentifier(cue, index, options);
    return [...head, timing + settings, vttText(cue.text, from, options)].join(
      "\n"
    );
  });
  return `WEBVTT\n\n${blocks.join("\n\n")}`;
}

export interface Checks {
  /** Cues whose start is before the previous cue's start. */
  outOfOrder: number;
  /** Cues that begin before the previous cue ends. */
  overlapping: number;
  /** Cues with no text. */
  empty: number;
  /** Cues whose end is not after their start. */
  zeroLength: number;
}

export function checkCues(cues: readonly SubtitleCue[]): Checks {
  const checks: Checks = {
    outOfOrder: 0,
    overlapping: 0,
    empty: 0,
    zeroLength: 0,
  };
  cues.forEach((cue, index) => {
    const previous = cues[index - 1];
    checks.empty += cue.text === "" ? 1 : 0;
    checks.zeroLength += cue.end <= cue.start ? 1 : 0;
    if (previous && cue.start < previous.start) {
      checks.outOfOrder += 1;
    } else if (previous && cue.start < previous.end) {
      checks.overlapping += 1;
    }
  });
  return checks;
}

export function isNumericId(id: string | null): boolean {
  return id !== null && RE_NUMERIC.test(id);
}
